"use client";

import { useRef } from "react";
import { Eyebrow, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger, useMaskedReveal } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import { ABUSE } from "./data";

/** Status pill styling — Live is brand pink (never green, per the page's
 *  brand rule), Roadmap is honest amber. */
const BADGES: Record<"Live" | "Roadmap", string> = {
  Live: "var(--color-brand-600)",
  Roadmap: "var(--color-data-amber)",
};

const badgeStyle = (badge: "Live" | "Roadmap") => {
  const color = BADGES[badge];
  return {
    color,
    background: `color-mix(in srgb, ${color} ${badge === "Live" ? 9 : 12}%, white)`,
    borderColor: `color-mix(in srgb, ${color} ${badge === "Live" ? 30 : 35}%, transparent)`,
  };
};

/**
 * Abuse & link safety — named mechanisms with their real status. A roadmap
 * row ships only with a dated lib/roadmap.ts entry behind it (see data.ts).
 */
export default function AbuseSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h2" });
  useEnterStagger(rootRef, "[data-abuse-row]", { y: 12, stagger: 0.06 });

  return (
    <SectionShell className="md:pt-px md:pb-px">
      <div ref={rootRef}>
        {/* One block — claim and status table share a single clearing. */}
        <div
          data-grid-block
          className="grid items-center gap-10 lg:grid-cols-[5fr_7fr] lg:gap-16"
        >
          <div ref={copyRef} className="min-w-0">
            <div data-head-sub className="flex">
              <Eyebrow>Abuse &amp; link safety</Eyebrow>
            </div>
            <h2 className="mt-5 text-[clamp(30px,4vw,48px)] font-bold leading-[1.06] tracking-[-0.027em]">
              What&rsquo;s live, by name.
            </h2>
            <p
              data-head-sub
              className="mt-[18px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-muted"
            >
              Named mechanisms only. Where something isn&rsquo;t live yet, it says
              so — this page never implies. Seen a bad link?{" "}
              <a
                href={routePath("reportAbuse")}
                className="underline underline-offset-2 transition-colors hover:text-brand-600"
              >
                Report it
              </a>
              .
            </p>
          </div>

          <div className="min-w-0 overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref">
            {ABUSE.map((row) => (
              <div
                key={row.name}
                data-abuse-row
                className="grid grid-cols-[1fr_auto] items-center gap-x-[18px] gap-y-1 border-t border-line px-6 py-[15px] first:border-t-0 md:grid-cols-[minmax(130px,180px)_minmax(0,1fr)_auto]"
              >
                <span className="text-[13.5px] font-bold">{row.name}</span>
                <span className="text-[12.5px] leading-[1.5] text-muted max-md:col-span-2 max-md:row-start-2">
                  {row.desc}
                </span>
                <span
                  className="justify-self-end whitespace-nowrap rounded-full border px-2.5 py-[3px] font-jetbrains text-[10px]"
                  style={badgeStyle(row.badge)}
                >
                  {row.badge}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
